import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CircularProgress from "@mui/material/CircularProgress";
import { useAuth0 } from "@auth0/auth0-react";
import FinanceService from "../../Lib/API/Finance";
import './tranzilaCard.scss';

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 3 * 60 * 1000; // 3 minutes — user needs time to fill card details

const TranzilaCard = ({
  terminalName,
  onSaveCard,
  onCharged,
  loading,
  setLoading,
  saveCard,
  amount,
  currency,
  email,
  contactName,
}) => {
  const iframeRef = useRef(null);
  const initialLoadedRef = useRef(false);
  const pollTimerRef = useRef(null);
  const pollDeadlineRef = useRef(null);

  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [thtk, setThtk] = useState('');
  const [handshakeError, setHandshakeError] = useState('');
  const [pollError, setPollError] = useState('');
  const { getAccessTokenSilently } = useAuth0();

  const currencyCode = useMemo(() => {
    if (currency === 'ILS' || currency === 'NIS') return '1';
    if (currency === 'USD') return '2';
    if (currency === 'EUR') return '978';
    if (currency === 'GBP') return '826';
    return '1';
  }, [currency]);

  // tranmode=AK authorizes + creates a token in one step (K alone errors on this terminal)
  const tranmode = saveCard ? 'AK' : 'A';
  const sumValue = saveCard ? 1 : Number(amount || 1);

  // Fetch handshake thtk from backend
  useEffect(() => {
    if (!terminalName) return;
    let cancelled = false;
    initialLoadedRef.current = false;
    setThtk('');
    setHandshakeError('');
    setPollError('');
    setIframeLoaded(false);
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        console.log('[TranzilaCard] requesting handshake, amount:', sumValue.toFixed(2));
        const result = await FinanceService.tranzilaHandshake(token, {
          amount: sumValue.toFixed(2),
        });
        console.log('[TranzilaCard] handshake result:', result);
        if (!cancelled) setThtk(result?.thtk || '');
      } catch (error) {
        console.error('[TranzilaCard] handshake failed:', error);
        if (!cancelled) setHandshakeError(error?.message || 'Handshake failed');
      }
    })();
    return () => { cancelled = true; };
  }, [terminalName, sumValue, getAccessTokenSilently]);

  const apiBase = process.env.REACT_APP_TRANZILA_CALLBACK_URL || process.env.REACT_APP_BASE_URL || '';

  const iframeSrc = useMemo(() => {
    if (!terminalName || !thtk) return '';
    const successUrl = `${apiBase}/finance/v1/payment/tranzila/result?thtk=${thtk}`;
    const failUrl = `${apiBase}/finance/v1/payment/tranzila/result?thtk=${thtk}&failed=1`;
    const notifyUrl = `${apiBase}/finance/v1/payment/tranzila/notify?thtk=${thtk}`;
    const params = new URLSearchParams({
      thtk,
      new_process: '1',
      sum: sumValue.toFixed(2),
      currency: currencyCode,
      cred_type: '1',
      tranmode,
      nologo: '1',
      lang: 'us',
      trBgColor: 'FFFFFF',
      trTextColor: '333333',
      trButtonColor: '2555EF',
      buttonLabel: saveCard ? 'Save Card' : 'Pay Now',
      success_url_address: successUrl,
      fail_url_address: failUrl,
      notify_url_address: notifyUrl,
    });
    if (email) params.set('contact', email);
    if (contactName) params.set('contact_name', contactName);
    return `https://direct.tranzila.com/${terminalName}/iframenew.php?${params.toString()}`;
  }, [terminalName, thtk, sumValue, currencyCode, tranmode, saveCard, email, contactName, apiBase]);

  useEffect(() => {
    if (iframeSrc) {
      console.log('[TranzilaCard] iframe src:', iframeSrc);
      // Reset load tracker and clear previous errors whenever the iframe URL changes
      initialLoadedRef.current = false;
      setIframeLoaded(false);
      setPollError('');
      clearTimeout(pollTimerRef.current);
    }
  }, [iframeSrc]);

  // Poll backend for result after iframe submission (second onLoad)
  const startPolling = useCallback(async () => {
    if (!thtk) return;
    console.log('[TranzilaCard] starting poll for thtk:', thtk);
    pollDeadlineRef.current = Date.now() + POLL_TIMEOUT_MS;

    const poll = async () => {
      if (Date.now() > pollDeadlineRef.current) {
        console.error('[TranzilaCard] poll timed out');
        setPollError('Payment result timed out. Please check if the payment was processed.');
        return;
      }
      try {
        const token = await getAccessTokenSilently();
        const response = await FinanceService.tranzilaIframePoll(token, thtk);
        console.log('[TranzilaCard] poll response:', response);
        if (response?.ready) {
          const result = response.result;
          console.log('[TranzilaCard] transaction result:', result);
          if (result?.failed === '1' || (result?.Response && result?.Response !== '000')) {
            setPollError(result?.error_msg || 'Payment failed');
            return;
          }
          const token = result?.TranzilaTK;
          if (onSaveCard && token) onSaveCard(token, result);
          if (onCharged) onCharged({ ...result, token });
        } else {
          pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
        }
      } catch (error) {
        console.error('[TranzilaCard] poll error:', error);
        setPollError('Could not retrieve payment result');
      }
    };
    poll();
  }, [thtk, getAccessTokenSilently, onSaveCard, onCharged]);

  // Clean up poll timer on unmount
  useEffect(() => {
    return () => clearTimeout(pollTimerRef.current);
  }, []);

  const handleIframeLoad = useCallback(() => {
    if (!initialLoadedRef.current) {
      // First load: iframe rendered the payment form
      console.log('[TranzilaCard] iframe loaded (initial)');
      initialLoadedRef.current = true;
      setIframeLoaded(true);
    } else {
      // Second load: iframe navigated to success/fail URL after submission
      console.log('[TranzilaCard] iframe loaded (post-submission) — starting poll');
      startPolling();
    }
  }, [startPolling]);

  if (!terminalName) {
    console.error('[TranzilaCard] terminal not configured');
    return <div className="tranzila-card__error">Tranzila terminal not configured</div>;
  }

  if (handshakeError) {
    return <div className="tranzila-card__error">Tranzila handshake failed: {handshakeError}</div>;
  }

  if (pollError) {
    return <div className="tranzila-card__error">{pollError}</div>;
  }

  return (
    <div className="tranzila-card">
      {(!iframeLoaded || !iframeSrc) && (
        <div className="tranzila-card__loading">
          <CircularProgress size={24} />
          <span>Loading payment form...</span>
        </div>
      )}
      {saveCard && (
        <p className="tranzila-card__note">
          A temporary {currencyCode === '1' ? '₪' : '$'}1 verification hold will be placed and automatically released.
        </p>
      )}
      <iframe
        ref={iframeRef}
        src={iframeSrc}
        title="Tranzila Payment"
        className="tranzila-card__iframe"
        onLoad={handleIframeLoad}
        onError={(e) => console.error('[TranzilaCard] iframe failed to load:', e)}
        allow="payment"
        sandbox="allow-scripts allow-same-origin allow-forms allow-top-navigation"
      />
    </div>
  );
};

export default TranzilaCard;
